import React, { useCallback } from 'react';
import { observer } from 'mobx-react';
import {
  useTheme,
  Box,
  Autocomplete,
  IconButton,
  InputAdornment,
  OutlinedInput,
  Tooltip,
  TextField,
  Checkbox,
  FormControlLabel,
  Typography,
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import useData from './hooks';
import { TextQcFieldName } from './const';
import { OptionsRender } from './types';
import { ValueFieldsTypesProps } from '50.quickConnect.Fields/FieldsTypes/types';
import QcTooltip from '50.quickConnect.Fields/QcTooltip';
import { EditDesc } from '90.quickConnect.Models/models';
import { FieldMainBox, FlexRow } from '50.quickConnect.Fields/styles';
import { DataSourceType } from '90.quickConnect.Models/enums';
import { LruToggleButton, LruHistoryPanel } from '50.quickConnect.Fields/LRU';
import { autoCompleteMinLength } from '50.quickConnect.Fields/const';
import AutoCompleteSearchButton from '50.quickConnect.Fields/AutoCompleteSearchButton';

const TextQcField = ({
  field,
  updateDeclaration,
  labelSpan,
  setSelectedIndex,
  isReadOnly,
  labelClass,
  selectedIndex,
}: ValueFieldsTypesProps) => {
  const editDesc = field as EditDesc;
  const { toolTip, id, lines, dataSourceType, dataSource, fullPathId } = editDesc;
  const nameWithId = `${TextQcFieldName}-${id}`;
  const {
    textLocalValue,
    t,
    clearState,
    outlinedInputChanged,
    autocompleteChanged,
    autocompleteInputChanged,
    onHistoryTagChange,
    autoCompleteProp,
    isEventClick,
    searchAllUO,
    handleOnChangeSearchAllUO,
    searchNow,
    requiredField,
    verifRequiredField,
    fieldFocused,
    onBlur,
    lruList,
    showLruList,
    toggleDisplayLruList,
    deleteLRUItem,
  } = useData(editDesc, updateDeclaration);

  const { breakpoints } = useTheme();

  const handleSelectField = useCallback(() => {
    if (!isReadOnly) setSelectedIndex(fullPathId);
  }, [isReadOnly, setSelectedIndex, fullPathId]);

  return (
    <Box
      className={labelClass}
      id={`scrollToAnchor-${fullPathId}`}
      data-cy={nameWithId}
      sx={FieldMainBox(breakpoints)}
      onClick={handleSelectField}
      onFocus={handleSelectField}
    >
      <Box sx={FlexRow}>
        <Box component="span" sx={{ flex: 1 }}>
          {labelSpan}
        </Box>
        {toolTip && <QcTooltip parentId={id} tooltip={toolTip} />}
      </Box>
      {dataSourceType === DataSourceType.Family &&
        dataSource &&
        dataSource.idDataSource &&
        dataSource.forceSearchAllUO === undefined && (
          <FormControlLabel
            sx={{ mb: '1.3em' }}
            control={
              <Checkbox checked={searchAllUO} data-cy={`${nameWithId}-checkox`} onChange={handleOnChangeSearchAllUO} />
            }
            label={t('formlib_mail_info_datasource_search_all_uo')}
            disabled={isReadOnly}
          />
        )}
      {dataSourceType === DataSourceType.Family && dataSource && dataSource.idDataSource ? (
        <Box>
          <Autocomplete
            data-cy={`${nameWithId}-ul`}
            value={textLocalValue}
            filterOptions={(x) => x}
            getOptionLabel={(option: string | OptionsRender) =>
              typeof option !== 'string' ? option.value : option ?? ''
            }
            fullWidth
            inputValue={textLocalValue}
            onChange={autocompleteChanged}
            onInputChange={autocompleteInputChanged}
            options={autoCompleteProp}
            freeSolo={true}
            open={
              textLocalValue.trim().length >= autoCompleteMinLength &&
              !isEventClick &&
              autoCompleteProp.length > 0 &&
              autoCompleteProp.every((v) => v !== null) &&
              selectedIndex === fullPathId
            }
            readOnly={isReadOnly}
            disabled={isReadOnly}
            clearOnBlur={true}
            renderOption={(props, option) => {
              return (
                <li
                  {...props}
                  key={(option as OptionsRender).id}
                  id={(option as OptionsRender).id}
                  data-cy={`${nameWithId}-li-${(option as OptionsRender).id}`}
                >
                  <div>
                    <Typography fontWeight="bold">{(option as OptionsRender).title}</Typography>
                    <Typography fontWeight="400">{(option as OptionsRender).description}</Typography>
                  </div>
                </li>
              );
            }}
            renderInput={(params) => (
              <Box sx={FlexRow}>
                <TextField
                  data-cy={`${nameWithId}-input`}
                  onBlur={onBlur}
                  inputRef={(input) => {
                    if (input != null && verifRequiredField && requiredField?.fullPathId === fullPathId) {
                      input.focus();
                    } else if (
                      input != null &&
                      !verifRequiredField &&
                      !requiredField &&
                      fieldFocused &&
                      fullPathId.includes(fieldFocused)
                    ) {
                      return input.focus();
                    }
                  }}
                  {...params}
                  sx={{ flex: 1 }}
                />
                <AutoCompleteSearchButton
                  dataCy={`${nameWithId}-search`}
                  onClick={searchNow}
                  isReadOnly={!!isReadOnly}
                />
                <LruToggleButton hasItems={lruList.length > 0} onClick={toggleDisplayLruList} disabled={isReadOnly} />
              </Box>
            )}
          />
          <LruHistoryPanel
            disabled={isReadOnly}
            show={showLruList}
            list={lruList}
            onTagChange={onHistoryTagChange}
            handleDeleteItem={deleteLRUItem}
            fieldFullId={fullPathId}
          />
        </Box>
      ) : dataSourceType === DataSourceType.AzureAD ? (
        <Box>
          <Autocomplete
            id={`${nameWithId}-autocomplete-azure`}
            value={textLocalValue}
            filterOptions={(x) => x}
            getOptionLabel={(option: string | OptionsRender) =>
              typeof option !== 'string' ? option.value : option ?? ''
            }
            fullWidth
            inputValue={textLocalValue}
            onChange={autocompleteChanged}
            onInputChange={autocompleteInputChanged}
            options={autoCompleteProp}
            freeSolo={true}
            open={
              textLocalValue.trim().length >= autoCompleteMinLength &&
              !isEventClick &&
              autoCompleteProp.length > 0 &&
              autoCompleteProp.every((v) => v !== null) &&
              selectedIndex === fullPathId
            }
            readOnly={isReadOnly}
            disabled={isReadOnly}
            clearOnBlur={true}
            renderOption={(props, option) => (
              <li
                {...props}
                key={(option as OptionsRender).id}
                id={(option as OptionsRender).id}
                data-cy={`${nameWithId}-li-${(option as OptionsRender).id}`}
              >
                <div>
                  <Typography fontWeight="bold">{(option as OptionsRender).title}</Typography>
                  <Typography fontWeight="400">{(option as OptionsRender).description}</Typography>
                </div>
              </li>
            )}
            renderInput={(params) => (
              <Box sx={FlexRow}>
                <TextField
                  data-cy={`${nameWithId}-input-autocomplete-azure`}
                  inputRef={(input) => {
                    if (input != null && verifRequiredField && requiredField?.fullPathId === fullPathId) {
                      input.focus();
                    } else if (
                      input != null &&
                      !verifRequiredField &&
                      !requiredField &&
                      fieldFocused &&
                      fullPathId.includes(fieldFocused)
                    ) {
                      return input.focus();
                    }
                  }}
                  {...params}
                  sx={{ flex: 1 }}
                />
                <AutoCompleteSearchButton
                  dataCy={`${nameWithId}-search`}
                  onClick={searchNow}
                  isReadOnly={!!isReadOnly}
                />
                <LruToggleButton hasItems={lruList.length > 0} onClick={toggleDisplayLruList} disabled={isReadOnly} />
              </Box>
            )}
          />
          <LruHistoryPanel
            disabled={isReadOnly}
            show={showLruList}
            list={lruList}
            onTagChange={onHistoryTagChange}
            handleDeleteItem={deleteLRUItem}
            fieldFullId={fullPathId}
          />
        </Box>
      ) : (
        <Box>
          <Box sx={FlexRow}>
            <OutlinedInput
              inputRef={(input) => {
                if (input != null && verifRequiredField && requiredField?.fullPathId === fullPathId) {
                  input.focus();
                } else if (
                  input != null &&
                  !verifRequiredField &&
                  !requiredField &&
                  fieldFocused &&
                  fullPathId === fieldFocused
                ) {
                  return input.focus();
                }
              }}
              data-cy={`${nameWithId}-input`}
              id={`${nameWithId}-input`}
              type={'text'}
              value={textLocalValue}
              placeholder={t("input_text'").toString()}
              required
              onBlur={onBlur}
              sx={{ flex: 1 }}
              onChange={outlinedInputChanged}
              multiline={lines && lines > 1 ? true : false}
              rows={lines && lines > 1 ? lines : undefined}
              inputProps={{
                readOnly: isReadOnly,
                disabled: isReadOnly,
              }}
              endAdornment={
                <InputAdornment position="end">
                  <Tooltip title={t('formlib_array_deleted_title').toString()}>
                    <span>
                      <IconButton data-cy={`${nameWithId}-clear`} edge="end" onClick={clearState} disabled={isReadOnly}>
                        <ClearIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                </InputAdornment>
              }
            />
            <LruToggleButton hasItems={lruList.length > 0} onClick={toggleDisplayLruList} disabled={isReadOnly} />
          </Box>
          <LruHistoryPanel
            disabled={isReadOnly}
            show={showLruList}
            list={lruList}
            onTagChange={onHistoryTagChange}
            handleDeleteItem={deleteLRUItem}
            fieldFullId={fullPathId}
          />
        </Box>
      )}
    </Box>
  );
};
export default observer(TextQcField);
